/**
 * Timezone Context
 *
 * 사용자의 timezone을 전역적으로 관리
 * 브라우저의 timezone을 자동으로 감지하고, 필요시 사용자가 변경 가능
 */

import { createContext, useState, useEffect, type ReactNode } from 'react';
import { getBrowserTimezone, getTimezoneInfo, type TimezoneInfo } from '@/utils/date';

interface TimezoneContextValue {
  /** 현재 사용중인 timezone (IANA format) */
  timezone: string;
  /** Timezone 정보 */
  timezoneInfo: TimezoneInfo;
  /** Timezone 변경 함수 (선택적 기능) */
  setTimezone: (timezone: string) => void;
  /** 브라우저 기본 timezone으로 리셋 */
  resetToDefault: () => void;
}

const TimezoneContext = createContext<TimezoneContextValue | undefined>(undefined);

interface TimezoneProviderProps {
  children: ReactNode;
  /** 초기 timezone (지정하지 않으면 브라우저 timezone 사용) */
  initialTimezone?: string;
}

export function TimezoneProvider({ children, initialTimezone }: TimezoneProviderProps) {
  const [timezone, setTimezoneState] = useState<string>(() => {
    // localStorage에서 사용자가 설정한 timezone 확인
    const savedTimezone = localStorage.getItem('user_timezone');
    return initialTimezone || savedTimezone || getBrowserTimezone();
  });

  const [timezoneInfo, setTimezoneInfo] = useState<TimezoneInfo>(getTimezoneInfo());

  // Timezone 변경 시 localStorage에 저장하고 info 업데이트
  const setTimezone = (newTimezone: string) => {
    setTimezoneState(newTimezone);
    localStorage.setItem('user_timezone', newTimezone);
    setTimezoneInfo(getTimezoneInfo());
  };

  // 브라우저 기본 timezone으로 리셋
  const resetToDefault = () => {
    const browserTimezone = getBrowserTimezone();
    setTimezoneState(browserTimezone);
    localStorage.removeItem('user_timezone');
    setTimezoneInfo(getTimezoneInfo());
  };

  // Timezone 변경 시 info 업데이트
  useEffect(() => {
    setTimezoneInfo(getTimezoneInfo());
  }, [timezone]);

  // 주기적으로 timezone info 업데이트 (1분마다)
  useEffect(() => {
    const interval = setInterval(() => {
      setTimezoneInfo(getTimezoneInfo());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  return (
    <TimezoneContext.Provider
      value={{
        timezone,
        timezoneInfo,
        setTimezone,
        resetToDefault,
      }}
    >
      {children}
    </TimezoneContext.Provider>
  );
}
